import { getSegmentIndexAtAngle } from './wheelRenderer';

export type SpinPhase = 'idle' | 'anticipating' | 'accelerating' | 'cruising' | 'decelerating' | 'settled';

export type SpinAnimationState = {
  phase: SpinPhase;
  rotation: number;
  angularVelocity: number;
};

type AnimationController = {
  startPreSpin: () => void;
  finishSpin: (targetSegmentIndex: number, segmentCount: number) => void;
  cancelSpin: () => void;
  setIdleEnabled: (enabled: boolean) => void;
  getState: () => SpinAnimationState;
  destroy: () => void;
};

type AnimationCallbacks = {
  onFrame: (rotation: number) => void;
  onTick: () => void;
  onSettle: () => void;
};

/** Anticipation: backward rock before acceleration */
const ANTICIPATION_DURATION_MS = 420;
const ANTICIPATION_ROCK_DEG = 18;

const ACCEL_DURATION_MS = 600;
const CRUISE_VELOCITY = 720; // deg/s
const DECEL_MIN_ROTATIONS = 3;
const DECEL_DURATION_MS = 3800;
const IDLE_VELOCITY = 16; // deg/s

function easeOutQuart(t: number): number {
  return 1 - (1 - t) ** 4;
}

function computeLandingRotation(
  currentRotation: number,
  targetSegmentIndex: number,
  segmentCount: number
): number {
  if (segmentCount === 0) return currentRotation;

  const sliceDeg = 360 / segmentCount;
  const segmentCenter = targetSegmentIndex * sliceDeg + sliceDeg / 2;
  // Landing position is the 3 o'clock (RIGHT middle) pointer: rotate so the
  // target segment's center lands at canvas angle 0° (+x direction).
  const targetOffset = ((90 - segmentCenter) + 360) % 360;
  const baseRotation = currentRotation - (currentRotation % 360) + targetOffset;
  const minRotation = currentRotation + DECEL_MIN_ROTATIONS * 360;

  let landing = baseRotation;
  while (landing < minRotation) {
    landing += 360;
  }

  const jitterRange = sliceDeg * 0.3;
  const jitter = (Math.random() - 0.5) * jitterRange;
  return landing + jitter;
}

export function createSpinAnimation(callbacks: AnimationCallbacks): AnimationController {
  const state: SpinAnimationState = { phase: 'idle', rotation: 0, angularVelocity: 0 };
  let rafId: number | null = null;
  let lastTimestamp: number | null = null;
  let phaseStartTime = 0;
  let lastTickIndex = -1;
  let pendingSegmentCount = 0;
  let idleEnabled = false;

  // Anticipation
  let anticipationStartRotation = 0;

  // Deceleration
  let decelStartRotation = 0;
  let decelTargetRotation = 0;

  function tick(timestamp: number): void {
    if (lastTimestamp === null) {
      lastTimestamp = timestamp;
    }
    const dt = Math.min((timestamp - lastTimestamp) / 1000, 0.05);
    lastTimestamp = timestamp;
    const elapsed = timestamp - phaseStartTime;

    if (state.phase === 'idle') {
      if (!idleEnabled) {
        state.angularVelocity = 0;
        callbacks.onFrame(state.rotation);
        rafId = null;
        return;
      }

      state.angularVelocity = IDLE_VELOCITY;
      state.rotation = (state.rotation + state.angularVelocity * dt) % 360;
    } else if (state.phase === 'anticipating') {
      const t = Math.min(elapsed / ANTICIPATION_DURATION_MS, 1);
      // Smooth backward rock then return: -sin(t * PI) goes 0 → -1 → 0
      state.rotation = anticipationStartRotation - ANTICIPATION_ROCK_DEG * Math.sin(t * Math.PI);
      state.angularVelocity = 0;

      if (t >= 1) {
        state.phase = 'accelerating';
        phaseStartTime = performance.now();
        lastTimestamp = null;
      }
    } else if (state.phase === 'accelerating') {
      const t = Math.min(elapsed / ACCEL_DURATION_MS, 1);
      state.angularVelocity = CRUISE_VELOCITY * easeOutQuart(t);
      state.rotation += state.angularVelocity * dt;

      if (t >= 1) {
        state.phase = 'cruising';
        state.angularVelocity = CRUISE_VELOCITY;
      }
    } else if (state.phase === 'cruising') {
      state.rotation += state.angularVelocity * dt;
    } else if (state.phase === 'decelerating') {
      const t = Math.min(elapsed / DECEL_DURATION_MS, 1);
      const eased = easeOutQuart(t);
      state.rotation = decelStartRotation + (decelTargetRotation - decelStartRotation) * eased;
      state.angularVelocity = CRUISE_VELOCITY * (1 - eased);

      if (t >= 1) {
        state.rotation = decelTargetRotation;
        state.angularVelocity = 0;
        state.phase = 'settled';
        callbacks.onFrame(state.rotation);
        callbacks.onSettle();
        rafId = null;
        return;
      }
    }

    // Tick detection (skip during anticipation)
    if (pendingSegmentCount > 0 && state.angularVelocity > 30) {
      const currentIndex = getSegmentIndexAtAngle(pendingSegmentCount, state.rotation);
      if (currentIndex !== lastTickIndex && currentIndex >= 0) {
        lastTickIndex = currentIndex;
        callbacks.onTick();
      }
    }

    callbacks.onFrame(state.rotation);
    rafId = requestAnimationFrame(tick);
  }

  function startLoop(): void {
    if (rafId !== null) return;
    lastTimestamp = null;
    rafId = requestAnimationFrame(tick);
  }

  function stopLoop(): void {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  return {
    startPreSpin() {
      stopLoop();
      idleEnabled = false;
      anticipationStartRotation = state.rotation;
      state.phase = 'anticipating';
      phaseStartTime = performance.now();
      lastTickIndex = -1;
      startLoop();
    },

    finishSpin(targetSegmentIndex: number, segmentCount: number) {
      pendingSegmentCount = segmentCount;

      // If still anticipating, skip to accelerating first
      if (state.phase === 'anticipating') {
        state.rotation = anticipationStartRotation;
        state.phase = 'accelerating';
        state.angularVelocity = CRUISE_VELOCITY * 0.3;
        phaseStartTime = performance.now() - ACCEL_DURATION_MS * 0.3;
        lastTimestamp = null;
      }

      // If still accelerating, let it cruise briefly before decelerating
      if (state.phase === 'accelerating') {
        state.phase = 'cruising';
        state.angularVelocity = CRUISE_VELOCITY;
        // Schedule deceleration after a brief cruise
        setTimeout(() => {
          if (state.phase !== 'cruising') return;
          decelStartRotation = state.rotation;
          decelTargetRotation = computeLandingRotation(state.rotation, targetSegmentIndex, segmentCount);
          state.phase = 'decelerating';
          phaseStartTime = performance.now();
          lastTimestamp = null;
        }, 400);
        return;
      }

      decelStartRotation = state.rotation;
      decelTargetRotation = computeLandingRotation(state.rotation, targetSegmentIndex, segmentCount);
      state.phase = 'decelerating';
      phaseStartTime = performance.now();
      lastTimestamp = null;
    },

    cancelSpin() {
      stopLoop();
      state.phase = 'idle';
      state.angularVelocity = 0;
      callbacks.onFrame(state.rotation);
      if (idleEnabled) {
        startLoop();
      }
    },

    setIdleEnabled(enabled: boolean) {
      idleEnabled = enabled;

      if (enabled) {
        if (state.phase === 'idle') {
          startLoop();
        }
        return;
      }

      if (state.phase === 'idle') {
        stopLoop();
        state.angularVelocity = 0;
        callbacks.onFrame(state.rotation);
      }
    },

    getState() {
      return { ...state };
    },

    destroy() {
      stopLoop();
    },
  };
}

/** Lazily-initialized AudioContext for tick/win sounds. */
let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      audioCtx = new AudioContext();
    }
    if (audioCtx.state === 'suspended') {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  } catch {
    return null;
  }
}

export function playTickSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = 1200;
  osc.type = 'sine';
  gain.gain.setValueAtTime(0.06, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.04);
}

export function playWinSound(): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = 'sine';
    const startTime = ctx.currentTime + i * 0.12;
    gain.gain.setValueAtTime(0.08, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
    osc.start(startTime);
    osc.stop(startTime + 0.3);
  });
}
